/**
 * FAB EV's purchase check (owner, 29 Oct 2026: "create a purchase verification column in fab ev in
 * which i will add the purchase data of fab ev vehicle fab ev bought from autozone so it will just
 * verify the vehicle coming in from autozone dispatch or in app are same and number is also same to
 * the app if not it will show what all chasis are not there in app but in purchase sheet").
 *
 * The purchase sheet is read, kept, and set against the app as it stands each time it is opened:
 * the book's own vehicles, and what Autozone's book says it dispatched to the company.
 */
import { answersInBook, isWalledBook, reachesBook } from './book-wall.js';
import { ORG_IDS, isHeadAdminDesk } from './domain.js';

/**
 * The books that check their purchases, each its own (owner, 30 Oct 2026: "purchase check should be
 * unique for each book"): a sheet put in from Bright on Wheels is Bright on Wheels', set against its
 * book and never shown in FAB EV's, and the other way round. Not the maker's — Autozone buys nothing
 * from itself — and never the practice book.
 */
export const PURCHASE_CHECK_BOOKS: readonly string[] = [ORG_IDS.bow, ORG_IDS.fabev, ORG_IDS.tvs];

type Checker = { email: string; organizationId: string; roleKey?: string | null };

/**
 * Who checks a book's purchases: the owner's desks; in FAB EV its head admin; in the other books
 * their administrators.
 */
export function checksPurchases(user: Checker | null | undefined, book: string): boolean {
  if (!user || !PURCHASE_CHECK_BOOKS.includes(book)) return false;
  if (isHeadAdminDesk(user)) return true;
  if (isWalledBook(book)) return answersInBook(user, book);
  return user.roleKey === 'ADMINISTRATOR' && reachesBook(user, book);
}

/** The books a sign-in checks purchases in. */
export function purchaseCheckBooks(user: Checker | null | undefined): string[] {
  return PURCHASE_CHECK_BOOKS.filter((book) => checksPurchases(user, book));
}

/** Where one chassis of the sheet stands in the app. */
export type PurchaseLineState =
  | 'IN_BOOK' // in the company's own book
  | 'AUTOZONE_ONLY' // Autozone's book dispatched it to the company, but the company's book does not have it
  | 'OTHER_BOOK' // in the app, but in another company's book
  | 'UNSURE' // more than one vehicle could be it, on the last ten characters — check by hand
  | 'RETURNED' // on the purchase return sheet: sent back, not part of the actual purchase
  | 'NOT_IN_APP'; // nowhere in the app

export const PURCHASE_LINE_LABELS: Record<PurchaseLineState, string> = {
  IN_BOOK: 'In the app',
  AUTOZONE_ONLY: 'Autozone sent it — missing from this book',
  OTHER_BOOK: 'In another book',
  UNSURE: 'More than one could be it — check by hand',
  RETURNED: 'Returned to Autozone',
  NOT_IN_APP: 'Not in the app',
};

export interface PurchaseCheckLine {
  /** As the sheet wrote it, squashed. */
  chassisNo: string;
  date: string | null;
  billNo: string | null;
  model: string | null;
  state: PurchaseLineState;
  /** The app's own spelling, when it has the vehicle. */
  appChassisNo: string | null;
  /** Where it is in the app, in words — the book, or Autozone's dispatch and its day. */
  where: string | null;
  /** Matched only on its last ten characters — worth a look. */
  loose: boolean;  /** Matched to the book's vehicle of the same serial, typed with a letter different there — how the app has it (30 Oct 2026). */
  near?: string | null;
}

export interface PurchaseCheckAppOnly {
  chassisNo: string;
  model: string | null;
  /** The day it came into the book. */
  arrivedAt: string | null;
  status: string;
}

export interface PurchaseCheckResult {
  id: string;
  filename: string;
  /** The Google Sheet it was read from, when it was. */
  link: string | null;
  uploadedAt: string;
  uploadedBy: string | null;
  /** The days the sheet covers, when it carries dates. */
  from: string | null;
  to: string | null;
  counts: {
    /** Every chassis the purchase sheet names. */
    sheet: number;
    /** …of which the purchase return sheet sends back (29 Oct 2026). */
    returned: number;
    /** The actual purchase: bought less returned. */
    actual: number;
    /** Returns the purchase sheet does not name. */
    returnsNotInSheet: number;
    repeated: number;
    unreadable: number;
    inBook: number;
    autozoneOnly: number;
    otherBook: number;
    notInApp: number;
    unsure: number;
    /** The book's vehicles that came in over the sheet's days (all of them when it has no dates). */
    bookInPeriod: number;
    /** …and how many of those the sheet does not name. */
    appOnly: number;
    /** What Autozone's book dispatched to the company over the same days. */
    autozoneDispatched: number;
  };
  lines: PurchaseCheckLine[];
  appOnly: PurchaseCheckAppOnly[];
  /** What "Add to FAB EV store" put in from this check (30 Oct 2026), each undoable while it has not moved. */
  added: Array<{ chassisNo: string; vehicleId: string; via: 'HANDOVER' | 'NEW'; at: string; by: string; undoneAt: string | null }>;
  /** The purchase return sheet put beside it, when there is one. */
  returns: { filename: string; link: string | null; count: number } | null;
  /** Returns the purchase sheet does not name, with where the app has them. */
  returnsNotInSheet: Array<{ chassisNo: string; date: string | null; billNo: string | null; model: string | null; where: string | null }>;
  unreadable: string[];
}

export interface PurchaseCheckUpload {
  id: string;
  filename: string;
  uploadedAt: string;
  uploadedBy: string | null;
  sheet: number;
}

/**
 * How far a book's purchases are checked, till today (owner, 30 Oct 2026: "in purchase check show
 * the date as well what dates purchase has been uploaded and what all are remaing till date and all
 * the purchase should be checked till date"). Across every kept check of the book: each sheet covers
 * the days from its first to its last purchase date; a day no sheet covers is not yet checked.
 */
export interface PurchaseCheckCoverage {
  /** Today, in India. */
  today: string;
  /** The day the book's first vehicle came in; null when it has none. */
  firstPurchase: string | null;
  /**
   * Every vehicle that came in up to this day lies inside an uploaded sheet's days — null when the
   * book's very first purchase day is not covered yet.
   */
  checkedTill: string | null;
  /** The last day any sheet reaches. */
  latestSheetDay: string | null;
  /** Each kept sheet, newest upload first; `from`/`to` null when it carries no dates. */
  sheets: Array<{ id: string; filename: string; uploadedAt: string; uploadedBy: string | null; chassis: number; from: string | null; to: string | null }>;
  /** The days the sheets cover between them, merged, oldest first. */
  covered: Array<{ from: string; to: string }>;
  /** Stretches from the first purchase to today that no sheet covers and the book has vehicles in. */
  gaps: Array<{ from: string; to: string; vehicles: number; firstArrival: string; lastArrival: string }>;
  counts: {
    /** The book's vehicles that came in up to today. */
    purchased: number;
    /** …on days a sheet covers. */
    covered: number;
    /** …on days no sheet covers. */
    notChecked: number;
    /** Vehicles in the book with no arrival on record — no day to check them by. */
    noArrivalDay: number;
  };
  /** The vehicles not yet checked, oldest first (at most `NOT_CHECKED_SHOWN`). */
  notChecked: Array<{ chassisNo: string; model: string | null; variant: string | null; arrivedAt: string; status: string; holder: string | null }>;
}

/** How many not-yet-checked vehicles the coverage lists; the count says how many there are. */
export const NOT_CHECKED_SHOWN = 5000;
